import { useEffect, useState, useRef, useMemo } from 'react';
import { useIO } from '../../hooks/use-io';
import { AnimatePresence, motion } from 'framer-motion';

const pathVariants = {
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
  },
};

export const Vine = ({ flip, delay = 0, isWide }) => {
  const ref = useRef();
  const [width, setWidth] = useState(1000);
  const entry = useIO(ref, true);
  const isVisible = !!entry?.isIntersecting;

  const updateWidth = () => {
    if (ref.current) {
      // resize line
      const parent = ref.current.parentElement;
      parent && setWidth(parent.getBoundingClientRect().width);

      // remove animated properties from line
      const line = ref.current.querySelector('.vine__line');
      line?.removeAttribute('stroke-dashoffset');
      line?.removeAttribute('stroke-dasharray');
    }
  };

  useEffect(() => {
    updateWidth();
    document.fonts.ready.then(updateWidth);
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [ref]);

  const duration = 1.2;
  // idk if memos are necessary...
  const positionA = useMemo(() => 15 + Math.floor(Math.random() * 10), []);
  const positionB = useMemo(() => 75 + Math.floor(Math.random() * 10), []);
  const positionC = useMemo(() => 35 + Math.floor(Math.random() * 30), []);

  // randomly get component
  const getPiece = () => {
    const multiplier = isWide ? 4 : 3;
    const n = Math.floor(Math.random() * multiplier);
    const Piece = {
      0: Leaf,
      1: Curly,
      2: Twig,
      3: Squiggle,
    }[n];

    return Piece;
  };

  const PieceA = useMemo(() => getPiece(), []);
  const PieceB = useMemo(() => {
    let Piece = getPiece();

    while (Piece === Twig && Piece === PieceA) {
      Piece = getPiece();
    }

    return Piece;
  }, []);
  const PieceC = useMemo(() => {
    const n = Math.floor(Math.random() * 3);

    return isWide && !!n ? getPiece() : () => null;
  }, []);

  return (
    <AnimatePresence>
      <motion.svg
        ref={ref}
        className="vine"
        viewBox={`0 0 ${width} 40`}
        width={`${width}px`}
        transition={{ delay }}
        strokeLinecap="round"
      >
        {isVisible && width && (
          <g style={{ transform: 'translate(0, 50%)' }}>
            <motion.path
              className="vine__line"
              variants={pathVariants}
              initial="hidden"
              animate="visible"
              transition={{
                default: { duration: 1.2, ease: 'easeInOut' },
                opacity: { duration: 0.01, delay },
              }}
              d={`M2 0 H ${width - 2}`}
            ></motion.path>

            <PieceA
              position={`${positionA}%`}
              delay={(positionA / 100 + 0.1) * duration}
              flip={flip}
            />
            <PieceB
              position={`${positionB}%`}
              delay={(positionB / 100 + 0.15) * duration}
              flip={flip}
            />
            <PieceC
              position={`${positionC}%`}
              delay={(positionC / 100 + 0.2) * duration}
              flip={flip}
            />
          </g>
        )}
      </motion.svg>
    </AnimatePresence>
  );
};

const Curly = ({ position, delay, flip }) => {
  const f =
    typeof flip !== 'undefined'
      ? flip
      : useMemo(() => Math.round(Math.random()) === 0, []);

  return (
    <motion.path
      style={{
        transform: `translate(${position}, ${f ? '1px' : '-1px'})${
          f ? ' scale(1, -1)' : 'scale(1, 1)'
        }`,
      }}
      className="vine__curly"
      variants={pathVariants}
      initial="hidden"
      animate="visible"
      transition={{
        duration: 0.8,
        ease: 'easeInOut',
        delay,
        opacity: { duration: 0.01, delay },
      }}
      d="M4.9,1.5c7.4-0.1,12.4,8.2,6.9,14c-2.5,2.5-6.4,2.5-8.9,0c0,0,0,0,0,0c-2-2-2-5.1,0-7.1
	c0,0,0,0,0,0c3.1-3.2,9,0.8,5.7,4.5"
    />
  );
};

const Leaf = ({ position, delay, flip }) => {
  const f =
    typeof flip !== 'undefined'
      ? flip
      : useMemo(() => Math.round(Math.random()) === 0, []);
  const x = position;
  const y = f ? '-14px' : '3px';
  const scale = f ? '1, -1' : '1, 1';

  return (
    <motion.g
      style={{
        transform: `translate(${x}, ${y}) scale(${scale})`,
        transformOrigin: f ? 'bottom left' : 'top left',
      }}
      initial={{
        transform: `translate(${x}, ${y}) scale(0, 0)`,
      }}
      animate={{
        transform: `translate(${x}, ${y}) scale(${scale})`,
      }}
      transition={{
        delay,
      }}
    >
      <path
        className="vine__leaf"
        d="M10.8,10.8V4.5C10.8,2,8.8,0,6.4,0H0 v6.4c0,2.5,2,4.5,4.5,4.5H10.8z"
      />
      <motion.path
        className="vine__leaf"
        variants={pathVariants}
        initial="hidden"
        animate="visible"
        transition={{
          duration: 0.4,
          ease: 'easeInOut',
          delay,
          opacity: { duration: 0.01, delay },
        }}
        d="M0,0l6.5,6.5L0,0z"
      />
    </motion.g>
  );
};

const Squiggle = ({ position, delay }) => {
  const offset = parseInt(position) > 50 ? 20 : 10;
  const x = `${parseInt(position) - offset}%`;

  return (
    <motion.path
      className="vine__squiggle"
      style={{
        transform: `translate(${x}, -7.5px) scale(-1, 1)`,
        transformOrigin: '16% center',
      }}
      variants={pathVariants}
      initial="hidden"
      animate="visible"
      transition={{
        duration: 1,
        ease: 'easeInOut',
        delay,
        opacity: { duration: 0.01, delay },
      }}
      d="M174.6,7.6c-2.7-3-5.4-6.1-10.8-6.1c-10.8,0-10.8,12.1-21.7,12.1S131.2,1.5,120.4,1.5s-10.8,12.1-21.7,12.1
    c-5.4,0-8.1-3-10.8-6.1c-2.7-3-5.4-6.1-10.8-6.1c-10.8,0-10.8,12.1-21.7,12.1S44.5,1.5,33.6,1.5S22.8,13.6,12,13.6
    c-5.4,0-8.1-3-10.8-6.1"
    />
  );
};

const Twig = ({ position, delay, flip }) => {
  const f =
    typeof flip !== 'undefined'
      ? flip
      : useMemo(() => Math.round(Math.random()) === 0, []);
  return (
    <motion.path
      className="vine__twig"
      style={{
        transform: `translate(${position}, ${f ? '1px' : '-1px'})${
          f ? ' scale(1, -1)' : 'scale(1, 1)'
        }`,
      }}
      variants={pathVariants}
      initial="hidden"
      animate="visible"
      transition={{
        duration: 0.4,
        ease: 'easeInOut',
        delay,
        opacity: { duration: 0.01, delay },
      }}
      d="M0,1.5c4.6,0,8.3,3.7,8.3,8.3"
    />
  );
};
